import React, { useState, createContext, useContext } from 'react';
export type Modifier = {
  id: string;
  name: string;
  price: number;
  active: boolean;
};
type Ctx = {
  modifiers: Modifier[];
  addModifier: (m: Omit<Modifier, 'id'>) => void;
  updateModifier: (id: string, patch: Partial<Modifier>) => void;
  deleteModifier: (id: string) => void;
  getModifier: (id: string) => Modifier | undefined;
};
const ModifiersContext = createContext<Ctx | null>(null);
const uid = () => Math.random().toString(36).slice(2, 9);
const seed: Modifier[] = [{
  id: 'mod_cheese',
  name: 'Extra cheese',
  price: 1.5,
  active: true
}, {
  id: 'mod_bacon',
  name: 'Extra bacon',
  price: 3.0,
  active: true
}, {
  id: 'mod_eggs',
  name: 'Extra eggs',
  price: 2.0,
  active: true
}, {
  id: 'mod_avocado',
  name: 'Avocado',
  price: 2.5,
  active: true
}, {
  id: 'mod_salad',
  name: 'Side salad',
  price: 3.0,
  active: true
}, {
  id: 'mod_hotsauce',
  name: 'Hot sauce',
  price: 0,
  active: true
}, {
  id: 'mod_ketchup',
  name: 'Ketchup',
  price: 0,
  active: true
}, {
  id: 'mod_salmon',
  name: 'Extra salmon',
  price: 4.0,
  active: true
}, {
  id: 'mod_feta',
  name: 'Add feta',
  price: 1.5,
  active: true
}, {
  id: 'mod_poached',
  name: 'Poached egg',
  price: 2.0,
  active: true
}, {
  id: 'mod_fries',
  name: 'French fries',
  price: 4.0,
  active: true
}, {
  id: 'mod_sweetfries',
  name: 'Sweet potato fries',
  price: 4.5,
  active: true
}];
export function ModifiersProvider({
  children
}: {
  children: ReactNode;
}) {
  const [modifiers, setModifiers] = useState<Modifier[]>(seed);
  const addModifier = (m: Omit<Modifier, 'id'>) => setModifiers(prev => [...prev, {
    ...m,
    id: `mod_${uid()}`
  }]);
  const updateModifier = (id: string, patch: Partial<Modifier>) => setModifiers(prev => prev.map(m => m.id === id ? {
    ...m,
    ...patch
  } : m));
  const deleteModifier = (id: string) => setModifiers(prev => prev.filter(m => m.id !== id));
  const getModifier = (id: string) => modifiers.find(m => m.id === id);
  return <ModifiersContext.Provider value={{
    modifiers,
    addModifier,
    updateModifier,
    deleteModifier,
    getModifier
  }} data-id="element-2560">
      {children}
    </ModifiersContext.Provider>;
}
export function useModifiers() {
  const ctx = useContext(ModifiersContext);
  if (!ctx) throw new Error('useModifiers must be used within ModifiersProvider');
  return ctx;
}